import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { formatINR } from '@/lib/formatters';
import type { CartSummary as CartSummaryType } from '@/types/cart';
import { cn } from '@/lib/cn';

interface CartSummaryProps {
  summary: CartSummaryType;
  /** When provided, the CTA submits this callback instead of routing. */
  onCheckout?: () => void;
  /** Hide the CTA — used in checkout review step. */
  hideCheckoutCta?: boolean;
  className?: string;
}

const FREE_SHIPPING_THRESHOLD = 99_900; // ₹999 in paise

export function CartSummary({
  summary,
  onCheckout,
  hideCheckoutCta = false,
  className,
}: CartSummaryProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isEmpty = summary.items.length === 0;
  const remainingForFreeShipping = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - summary.subtotal
  );

  const ctaLabel = isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout';

  return (
    <aside
      className={cn(
        'flex flex-col gap-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-5 backdrop-blur-md',
        className
      )}
      aria-label="Order summary"
    >
      <h3 className="font-[family-name:var(--font-editorial)] text-lg text-white">
        Order Summary
      </h3>

      <dl className="flex flex-col gap-2 text-[14px]">
        <div className="flex items-baseline justify-between">
          <dt className="text-[color:var(--color-text-secondary)]">
            Subtotal ({summary.totalItems} item{summary.totalItems === 1 ? '' : 's'})
          </dt>
          <dd className="font-medium text-white">{formatINR(summary.subtotal)}</dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-[color:var(--color-text-secondary)]">GST</dt>
          <dd className="text-white">{formatINR(summary.gstAmount)}</dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-[color:var(--color-text-secondary)]">Shipping</dt>
          <dd className="text-white">
            {summary.shippingAmount === 0 ? 'FREE' : formatINR(summary.shippingAmount)}
          </dd>
        </div>
        {summary.discountAmount > 0 && (
          <div className="flex items-baseline justify-between">
            <dt className="text-[color:var(--color-success)]">
              Discount
              {summary.appliedCouponCode && (
                <span className="ml-1 text-xs text-[color:var(--color-text-muted)]">
                  ({summary.appliedCouponCode})
                </span>
              )}
            </dt>
            <dd className="text-[color:var(--color-success)]">
              -{formatINR(summary.discountAmount)}
            </dd>
          </div>
        )}

        <div className="my-1 border-t border-white/[0.06]" />

        <div className="flex items-baseline justify-between">
          <dt className="text-label-mono text-[color:var(--color-text-muted)]">Total</dt>
          <dd className="font-[family-name:var(--font-display)] text-2xl text-white">
            {formatINR(summary.total)}
          </dd>
        </div>
      </dl>

      {remainingForFreeShipping > 0 && summary.subtotal > 0 && (
        <p className="rounded-md bg-[color:var(--color-gold)]/10 px-3 py-2 text-xs text-[color:var(--color-gold-light)]">
          Add {formatINR(remainingForFreeShipping)} more for FREE delivery
        </p>
      )}

      {!hideCheckoutCta && (
        <>
          {isAuthenticated ? (
            <Button
              variant="primary"
              block
              size="lg"
              onClick={onCheckout}
              disabled={isEmpty}
            >
              {ctaLabel}
            </Button>
          ) : (
            <Link
              to="/login?redirectTo=%2Fcart"
              className="inline-flex h-14 items-center justify-center rounded-md bg-[color:var(--color-brand-magenta)] px-8 text-[15px] font-medium text-white shadow-[var(--shadow-magenta-glow)] transition-colors hover:bg-[color:var(--color-brand-magenta-light)]"
            >
              {ctaLabel}
            </Link>
          )}
        </>
      )}
    </aside>
  );
}
